import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { MACHINE_GROUP_DETAILS } from 'src/router';
import { generatePath } from 'src/utils';
import PageCard from 'src/components/PageCard';
import {
  PortalRole,
  useDeleteMachineGroupMutation,
  useListMachineGroupsQuery,
  UserProfile,
} from 'src/backend';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import LoadingIconButton from 'src/components/LoadingIconButton';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LoadingButton from 'src/components/LoadingButton';
import RoleBased from 'src/components/RoleBased';
import { useSelector } from 'react-redux';
import { getUserProfile } from 'src/components/UserStore/userProfileSlice';
import CreateEditMachineGroupForm from './CreateEditMachineGroupForm';

export type MachineGroup = {
  id: string;
  name: string;
  customerName: string;
  machineType: string;
  machinesCount: number;
  customerId: string;
  isDeleted: boolean;
  createdAt: string;
  createdBy: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
};

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onSearch: (searchText: string | undefined) => void;
    onFilterOnline: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }
}

function CustomToolbar(props: {
  onSearch: (searchText: string | undefined) => void;
}) {
  const { onSearch } = props;
  const [searchText, setSearchText] = useState('');
  return (
    <Stack direction="row-reverse">
      <IconButton
        onClick={() => onSearch(searchText === '' ? undefined : searchText)}
      >
        <Search />
      </IconButton>
      <TextField
        name="search"
        label="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSearch(searchText === '' ? undefined : searchText);
          }
        }}
      />
    </Stack>
  );
}

function MachineGroups() {
  const navigate = useNavigate();
  const userProfile = useSelector(getUserProfile) as UserProfile;

  const [refetchRequired, setRefetchRequired] = useState(false);
  const [isPopupOpen, openPopup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<MachineGroup | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleOpenPopup = useCallback(
    (isOpen: boolean, group: MachineGroup | null) => {
      setSelectedGroup(group);
      openPopup(isOpen);
    },
    [setSelectedGroup, openPopup],
  );

  const deleteMachineGroup = useDeleteMachineGroupMutation();
  const handleDelete = useCallback(() => {
    deleteMachineGroup[0](selectedGroup?.id)
      .unwrap()
      .then(() => {
        setRefetchRequired(true);
      })
      .finally(() => {
        setSelectedGroup(null);
        setShowConfirmationModal(false);
      });
  }, [deleteMachineGroup, selectedGroup]);

  const columns: GridColDef<MachineGroup>[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'machineType',
      headerName: 'Machine Type',
      flex: 1,
    },
    {
      field: 'customerId',
      headerName: 'Customer ID',
      flex: 1,
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      flex: 1,
    },
    {
      field: 'machinesCount',
      headerName: 'Machines Count',
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <RoleBased
          requiredRole={PortalRole[PortalRole.Admin]}
          component={
            <>
              <LoadingIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedGroup(params.row);
                  setShowConfirmationModal(true);
                }}
                loading={deleteMachineGroup[1].isLoading}
              >
                <Tooltip title="Delete group">
                  <DeleteOutlineOutlinedIcon />
                </Tooltip>
              </LoadingIconButton>
              <LoadingIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPopup(true, params.row);
                }}
              >
                <Tooltip title="Edit machine group">
                  <EditOutlinedIcon />
                </Tooltip>
              </LoadingIconButton>
            </>
          }
        />
      ),
    },
  ];

  const filter = useRef<string | undefined>(undefined);

  const [pageState, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [rowCountState, setRowCountState] = useState({
    rowCount: 10,
    totalRowCount: -1,
    topPage: -1,
  });

  const { data, isLoading, isFetching, refetch } = useListMachineGroupsQuery({
    filter: filter.current,
    page: pageState.page,
    take: pageState.pageSize,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (refetchRequired) {
      setRefetchRequired(false);
      refetch();
    }
  }, [refetchRequired, refetch]);

  useEffect(() => {
    if (!isLoading && !isFetching && pageState.page >= rowCountState.topPage) {
      const previousPagesCount = pageState.page * pageState.pageSize;
      const currentPageCount = data?.data.length ?? 0;
      const nextPage = data?.data.length === pageState.pageSize ? 1 : 0;
      const rowCount = previousPagesCount + currentPageCount + nextPage;
      let totalRowCount = -1;
      if (currentPageCount < pageState.pageSize) {
        totalRowCount = previousPagesCount + currentPageCount;
      }

      setRowCountState({
        rowCount,
        totalRowCount: totalRowCount ?? -1,
        topPage: pageState.page,
      });
    }
  }, [
    rowCountState.topPage,
    isLoading,
    isFetching,
    pageState.page,
    pageState.pageSize,
    data?.data.length,
  ]);

  const onPaginationModelChange = (m: GridPaginationModel) => {
    if (m.page !== pageState.page) {
      setPaginationModel({
        page: m.page,
        pageSize: pageState.pageSize,
      });
    }
  };

  const resetPaging = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setRowCountState({
      rowCount: 10,
      totalRowCount: -1,
      topPage: -1,
    });
  };

  const onSearch = (searchText: string | undefined) => {
    if (searchText !== filter.current) {
      filter.current = searchText;
      resetPaging();
    }
  };

  return (
    <>
      <Dialog
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
      >
        <DialogTitle sx={{ padding: '2rem', fontSize: '2.5rem' }}>
          Delete machine group
        </DialogTitle>
        <DialogContentText sx={{ padding: '2rem', fontSize: '1.5rem' }}>
          Are you sure you want to delete machine group:{' '}
          <b>{selectedGroup?.name}</b>
          ?
          <br />
          This action cannot be undone!
        </DialogContentText>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedGroup(null);
              setShowConfirmationModal(false);
            }}
            autoFocus
          >
            Cancel
          </Button>
          <LoadingButton
            loading={deleteMachineGroup[1].isLoading}
            variant="outlined"
            type="submit"
            onClick={handleDelete}
            sx={{
              background: '#f10101',
              color: 'white',
              '&:hover': {
                background: '#ab0e0e',
                boxShadow:
                  '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <CreateEditMachineGroupForm
        isOpen={isPopupOpen}
        group={selectedGroup}
        closeForm={(refetchRequired: boolean) => {
          handleOpenPopup(false, null);
          setRefetchRequired(refetchRequired);
        }}
      />
      <PageCard
        title="Machine groups"
        icon={<TableRowsOutlinedIcon />}
        sx={{
          '& .MuiCardHeader-root': {
            height: '80px',
          },
        }}
        contentSx={{ display: 'block' }}
        action={
          <>
            <RoleBased
              requiredRole={PortalRole[PortalRole.Admin]}
              component={
                <IconButton onClick={() => handleOpenPopup(true, null)}>
                  <Tooltip title="Add group">
                    <AddCircleOutlineOutlinedIcon />
                  </Tooltip>
                </IconButton>
              }
            />

            <LoadingIconButton
              loading={isLoading || isFetching}
              onClick={() => setRefetchRequired(true)}
            >
              <Tooltip title="Refresh">
                <CachedOutlinedIcon fontSize="large" />
              </Tooltip>
            </LoadingIconButton>
          </>
        }
      >
        <DataGrid
          classes={{ row: 'selectable' }}
          columns={columns}
          rows={data?.data ?? []}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false,
                customerId: false,
                action: userProfile?.role === PortalRole[PortalRole.Admin],
              },
            },
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          loading={isFetching}
          onRowClick={({ id }) =>
            navigate(generatePath(MACHINE_GROUP_DETAILS, { id }))
          }
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              onSearch,
            },
            pagination: { count: rowCountState.totalRowCount },
          }}
          sx={{
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              display: 'none',
            },
            '& .MuiDataGrid-overlay': {
              height: '100%',
            },
            '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus, &.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within':
              {
                outline: 'none',
              },
          }}
          rowCount={rowCountState.rowCount}
          paginationMode="server"
          filterMode="server"
          onPaginationModelChange={onPaginationModelChange}
          paginationModel={pageState}
          pageSizeOptions={[10]}
        />
      </PageCard>
    </>
  );
}

export default MachineGroups;
